import { MutableRefObject, useMemo } from 'react';

import { HStack, useHStack } from '@direct-frontend/components';
import cn from 'classnames';

import type { BaseRowData, LegacyDataProvider } from '@adv-frontend/data-provider';
import type { ColumnHeaderWrapperTemplateProps, GridTable, GridHeader } from '../../../types';
import type { GridRowSelectionManager } from '../../../lib/selectionManager/selectionManager';
import { Resizer } from '../Resizer/Resizer';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `HeaderCell`.
 *
 * @typeParam RowData Данные грида.
 */
type HeaderCellProps<RowData extends BaseRowData> = {
    /**
     * Инстанс шапки колонки.
     */
    header: GridHeader<RowData>;
    /**
     * Инстанс грида.
     */
    grid: GridTable<RowData>;
    /**
     * Ссылка на scrollable элемент грида.
     */
    gridRef?: MutableRefObject<HTMLDivElement | null>;
    /**
     * Ссылка на глобальный ресайзер грида.
     */
    commonResizerRef?: MutableRefObject<HTMLDivElement | null>;
    /**
     * Смещение слева для группы колонок.
     */
    groupLeftOffset?: number;
    /**
     * Смещение сверху для строки, в которой находится заголовок.
     */
    rowTopOffset?: number;
    /**
     * Признак подзаголовка грида.
     */
    isSubHeader?: boolean;
    /**
     * Признак видимости ресайзера колонки. (default = true)
     */
    isVisibleResizer?: boolean;
    /**
     * Датапровайдер
     */
    dataProvider: LegacyDataProvider<RowData>;
    /**
     * Менеджер выделения строк.
     */
    selectionManager: GridRowSelectionManager | undefined;
    /**
     * Высота ячейки с плейсхолдером (заглушкой).
     * В качестве значения используется высота ячейки нижнего уровня.
     */
    placeholderHeight?: number;
};

function defaultRenderHeaderCellWrapper({
    children,
    className,
    style,
    'data-testid': dataTestId,
}: ColumnHeaderWrapperTemplateProps) {
    return (
        <div className={className} style={style} data-testid={dataTestId}>
            {children}
        </div>
    );
}

/**
 * Компонент ячейки в шапке грида.
 *
 * @typeParam RowData Данные грида.
 */
export function HeaderCell<RowData extends BaseRowData>({
    commonResizerRef,
    grid,
    gridRef,
    groupLeftOffset,
    header,
    isSubHeader = false,
    rowTopOffset,
    isVisibleResizer = true,
    dataProvider,
    selectionManager,
    placeholderHeight = 0,
}: HeaderCellProps<RowData>) {
    const width = header.getSize();
    const inlineStyles = useMemo(
        () => ({ width, ...(header.isPlaceholder && { height: placeholderHeight }) }),
        [width, header.isPlaceholder, placeholderHeight]
    );
    const stackClasses = useHStack({});
    const emptyHeader = () => null;
    const render = isSubHeader
        ? header.column.columnDef.renderSubHeader
        : header.column.columnDef.renderHeader || emptyHeader;
    const renderWrapper =
        (isSubHeader ? header.column.columnDef.renderSubHeaderWrapper : header.column.columnDef.renderHeaderWrapper) ??
        defaultRenderHeaderCellWrapper;
    const hasRightBorder = header.column.columnDef.hasDelimiter;

    if (isSubHeader && !render) {
        return null;
    }

    return renderWrapper({
        children: (
            <>
                {!header.isPlaceholder && (
                    <HStack className={styles.headerCellInner}>
                        {render?.({ ...header.getContext(), dataProvider, selectionManager }) ?? null}
                    </HStack>
                )}
                {header.column.getCanResize() ? (
                    <Resizer
                        commonResizerRef={commonResizerRef}
                        header={header}
                        grid={grid}
                        gridRef={gridRef}
                        groupLeftOffset={groupLeftOffset}
                        rowTopOffset={rowTopOffset}
                        resizerOffset={
                            header.column.getIsResizing() ? grid.getState().columnSizingInfo.deltaOffset : null
                        }
                        isVisible={isVisibleResizer}
                    />
                ) : null}
            </>
        ),
        className: cn(stackClasses, styles.headerCell, hasRightBorder ? styles.rightBorder : undefined),
        style: inlineStyles,
        'data-testid': `Grid.HeaderCell-${header.id}`,
    });
}
